/**
 * /knowledge-base — KB landing page (Milestone 1).
 *
 * Filter ribbon · sortable columns · category tree sidebar · status-filtered.
 * Manager+ sees create/edit/archive controls.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useClient } from "@/context/ClientContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Search, Plus, BookOpen, Folder, ChevronRight,
  ArrowUpDown, ArrowUp, ArrowDown, Eye, ArchiveX, FileText, Filter as FilterIcon,
  Star, X, Bookmark, BarChart3,
} from "lucide-react";
import KbCategoryTree from "@/components/kb/KbCategoryNode";
import KbSearchInput from "@/components/kb/KbSearchInput";
import { toast } from "sonner";

const SORTABLE = [
  { key: "updated_at", label: "Updated" },
  { key: "title",      label: "Title" },
  { key: "view_count", label: "Views" },
];

const STATUS_TONES = {
  published: "bg-emerald-50 text-emerald-700 border-emerald-200",
  draft:     "bg-amber-50 text-amber-700 border-amber-200",
  archived:  "bg-slate-100 text-slate-500 border-slate-200",
};

export default function KnowledgeBase() {
  const navigate = useNavigate();
  const { user, hasPerm } = useAuth();
  const { activeSlug } = useClient();
  const canCreate = hasPerm("kb.create");
  const canManageCategories = hasPerm("kb.manage_categories");
  const canViewAnalytics = hasPerm("kb.view_analytics");
  const [searchParams, setSearchParams] = useSearchParams();

  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    q: searchParams.get("q") || "",
    status: searchParams.get("status") || "published",
    category_id: searchParams.get("category_id") || "",
    sort: "updated_at",
    dir: "desc",
  });
  const [expandedCats, setExpandedCats] = useState(new Set());
  const [savedSearches, setSavedSearches] = useState(() => loadSavedSearches());

  // Persist filter state to URL whenever it changes
  useEffect(() => {
    const next = {};
    if (filters.q) next.q = filters.q;
    if (filters.category_id) next.category_id = filters.category_id;
    if (filters.status && filters.status !== "published") next.status = filters.status;
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.q, filters.category_id, filters.status]);

  const saveCurrentSearch = () => {
    if (!filters.q && !filters.category_id && filters.status === "published") {
      toast.error("Nothing to save — apply a filter first.");
      return;
    }
    const label = window.prompt("Name this search", filters.q || "Filtered view");
    if (!label) return;
    const next = [{ id: String(Date.now()), label, snapshot: { q: filters.q, status: filters.status, category_id: filters.category_id } }, ...savedSearches].slice(0, 12);
    setSavedSearches(next);
    persistSavedSearches(next);
    toast.success(`Saved: ${label}`);
  };
  const applySavedSearch = (s) => setFilters((f) => ({ ...f, ...s.snapshot }));
  const removeSavedSearch = (id) => {
    const next = savedSearches.filter((s) => s.id !== id);
    setSavedSearches(next);
    persistSavedSearches(next);
  };

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: 100, offset: 0, sort: filters.sort, dir: filters.dir };
    if (filters.q) params.q = filters.q;
    if (filters.status) params.status = filters.status;
    if (filters.category_id) params.category_id = filters.category_id;
    api.get("/kb", { params })
      .then((r) => { setArticles(r.data.rows || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  }, [filters, activeSlug]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api.get("/kb-categories").then((r) => setCategories(r.data || []));
  }, [activeSlug]);

  const toggleSort = (col) => {
    setFilters((f) => f.sort === col
      ? { ...f, dir: f.dir === "asc" ? "desc" : "asc" }
      : { ...f, sort: col, dir: col === "title" ? "asc" : "desc" });
  };

  const onPickCategory = (id) => {
    setFilters((f) => ({ ...f, category_id: id }));
  };

  return (
    <div className="space-y-4" data-testid="kb-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-indigo-600" /> Knowledge Base
            <span className="text-xs text-slate-400 font-normal">{total} article{total === 1 ? "" : "s"}</span>
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">Playbooks, SOPs and FAQs shared across the team.</p>
        </div>
        {(canViewAnalytics || canCreate) && (
          <div className="flex gap-2">
            {canViewAnalytics && (
              <Link to="/knowledge-base/analytics">
                <Button variant="outline" data-testid="kb-analytics-link">
                  <BarChart3 className="w-4 h-4 mr-1.5" /> Analytics
                </Button>
              </Link>
            )}
            {canCreate && (
              <Button onClick={() => navigate("/knowledge-base/new")}
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="kb-new-article-btn">
                <Plus className="w-4 h-4 mr-1.5" /> New article
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4">
        <Card className="border-slate-200 h-fit" data-testid="kb-sidebar">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-sm uppercase tracking-wider text-slate-500 flex items-center justify-between">
              <span className="flex items-center gap-1.5"><Folder className="w-3.5 h-3.5" /> Categories</span>
              {canManageCategories && (
                <Link to="/knowledge-base/categories" className="text-[10px] text-indigo-600 hover:text-indigo-800 normal-case font-normal"
                      data-testid="manage-categories-link">
                  Manage
                </Link>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="-mt-2 px-2 pb-3 space-y-0.5">
            <button type="button" onClick={() => onPickCategory("")}
                    className={`w-full text-left px-2 py-1.5 rounded text-xs flex items-center gap-2 ${
                      !filters.category_id ? "bg-indigo-50 text-indigo-700 font-medium" : "hover:bg-slate-100 text-slate-700"
                    }`}
                    data-testid="kb-category-all">
              <BookOpen className="w-3.5 h-3.5" /> All categories
            </button>
            <KbCategoryTree rows={categories}
                            expanded={expandedCats} setExpanded={setExpandedCats}
                            activeId={filters.category_id} onPick={onPickCategory} />
            {categories.length === 0 && (
              <div className="text-[11px] text-slate-400 italic px-2 py-1">No categories yet</div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-3 min-w-0">
          <Card className="border-slate-200">
            <CardContent className="p-3 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <KbSearchInput
                  value={filters.q}
                  onChange={(v) => setFilters((f) => ({ ...f, q: v }))}
                  status={filters.status === "all" ? "all" : filters.status}
                  testid="kb-search"
                />
                <div className="flex items-center gap-1.5">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Status</Label>
                  <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                    <SelectTrigger className="h-9 text-xs w-32" data-testid="kb-status-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="published" className="text-xs">Published</SelectItem>
                      <SelectItem value="draft" className="text-xs">Drafts</SelectItem>
                      <SelectItem value="archived" className="text-xs">Archived</SelectItem>
                      <SelectItem value="all" className="text-xs">All</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={saveCurrentSearch}
                        data-testid="kb-save-search-btn">
                  <Bookmark className="w-3.5 h-3.5 mr-1" /> Save search
                </Button>
                {(filters.q || filters.category_id || filters.status !== "published") && (
                  <Button variant="ghost" size="sm" className="text-slate-500 h-8"
                          onClick={() => setFilters({ q: "", status: "published", category_id: "", sort: "updated_at", dir: "desc" })}
                          data-testid="kb-filters-reset">
                    <FilterIcon className="w-3.5 h-3.5 mr-1" /> Reset
                  </Button>
                )}
              </div>
              {savedSearches.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-slate-100"
                     data-testid="kb-saved-searches">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mr-1">
                    <Star className="w-2.5 h-2.5 inline-block mr-0.5 mb-0.5" /> Saved
                  </span>
                  {savedSearches.map((s) => (
                    <Badge key={s.id} variant="outline"
                           className="bg-slate-50 text-slate-700 border-slate-200 gap-1 cursor-pointer hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-700"
                           data-testid={`kb-saved-search-${s.id}`}>
                      <button type="button" onClick={() => applySavedSearch(s)}>{s.label}</button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); removeSavedSearch(s.id); }}
                              className="text-slate-400 hover:text-rose-500 ml-0.5"
                              data-testid={`kb-saved-search-remove-${s.id}`}>
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortHead col="title"      filters={filters} onSort={toggleSort} />
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500">Category</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500">Tags</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                      <SortHead col="view_count" filters={filters} onSort={toggleSort} className="text-right" />
                      <SortHead col="updated_at" filters={filters} onSort={toggleSort} />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading && articles.length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-400 py-12">Loading…</TableCell></TableRow>
                    )}
                    {!loading && articles.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-sm text-slate-400 py-12">
                          <FileText className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                          No articles match these filters.
                          {canCreate && (
                            <div className="mt-3">
                              <Button onClick={() => navigate("/knowledge-base/new")} size="sm"
                                      className="bg-indigo-600 hover:bg-indigo-700"
                                      data-testid="kb-empty-new-btn">
                                <Plus className="w-3.5 h-3.5 mr-1" /> Create first article
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                    {articles.map((a) => (
                      <TableRow key={a.id} className="hover:bg-slate-50 cursor-pointer"
                                onClick={() => navigate(`/knowledge-base/${a.id}`)}
                                data-testid={`kb-row-${a.id}`}>
                        <TableCell className="font-medium text-sm text-slate-800 max-w-[360px]">
                          <Link to={`/knowledge-base/${a.id}`} onClick={(e) => e.stopPropagation()}
                                className="hover:text-indigo-700">{a.title}</Link>
                          {a.summary && <div className="text-[11px] text-slate-500 mt-0.5 truncate">{a.summary}</div>}
                        </TableCell>
                        <TableCell className="text-xs text-slate-600">{a.category || "—"}</TableCell>
                        <TableCell className="max-w-[180px]">
                          <div className="flex flex-wrap gap-1">
                            {(a.tags || []).slice(0, 3).map((t) => (
                              <Badge key={t} variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px]">{t}</Badge>
                            ))}
                            {(a.tags || []).length > 3 && (
                              <span className="text-[10px] text-slate-500">+{a.tags.length - 3}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-[10px] capitalize ${STATUS_TONES[a.status] || ""}`}>
                            {a.status === "archived" && <ArchiveX className="w-2.5 h-2.5 mr-0.5" />}
                            {a.status || "published"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          <span className="text-slate-600 inline-flex items-center gap-1">
                            <Eye className="w-3 h-3 text-slate-400" />
                            {a.view_count || 0}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">
                          {a.updated_at ? new Date(a.updated_at).toLocaleDateString() : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function SortHead({ col, filters, onSort, className = "" }) {
  const meta = SORTABLE.find((s) => s.key === col);
  const active = filters.sort === col;
  const Icon = !active ? ArrowUpDown : (filters.dir === "asc" ? ArrowUp : ArrowDown);
  return (
    <TableHead className={className}>
      <button type="button" onClick={() => onSort(col)} data-testid={`kb-sort-${col}`}
              className={`inline-flex items-center gap-1 text-xs uppercase tracking-wider ${
                active ? "text-indigo-600 font-bold" : "text-slate-500 hover:text-slate-700"
              }`}>
        {meta?.label || col}
        <Icon className="w-3 h-3" />
      </button>
    </TableHead>
  );
}

const SAVED_KEY = "kippu_kb_saved_searches_v1";
function loadSavedSearches() {
  try {
    const raw = localStorage.getItem(SAVED_KEY);
    if (!raw) return [];
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v : [];
  } catch { return []; }
}
function persistSavedSearches(list) {
  try { localStorage.setItem(SAVED_KEY, JSON.stringify(list)); } catch { /* quota */ }
}
